import type { FC } from "react";
import { Fragment } from "react";

import dayjs from "dayjs";

import type { BacklinksTimeseriesListData, BacklinksTimeSeriesSummary } from "@similarweb/pro-api-client";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import {
    API_DATE_REQUEST_FORMAT,
    START_DATE_13_M,
    TODAY,
} from "app/pages/digital-marketing/backlinks/shared/BacklinksConsts";
import { useExpectedBacklinksParams } from "app/pages/digital-marketing/backlinks/shared/BacklinksUtils";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { useBacklinksTimeseriesApi } from "../../../../shared/backlinks-api-service";
import {
    BacklinksOverviewTopWidgetsContainer,
    BacklinksOverviewTopWidgetsEmptyContainer,
} from "../../BacklinksOverviewStyledComponents";
import { BacklinksSingleMetricWidget } from "../SingleMetricWidget/BacklinksSingleMetricWidget";

const getTodayItemOrClosestWithData = (data: BacklinksTimeseriesListData = []) => {
    const reversedData = [...data].reverse();
    const todayItem = reversedData.find((item) => dayjs.utc(item.Date).isSame(TODAY, "day"));
    // We return the item only if it has all the required data and not 0
    if (todayItem && todayItem.Backlinks && todayItem.ReferringDomains && todayItem.Rank) {
        return todayItem;
    }

    // If previous day is not available, we return the closest day with data
    // If this one item also has no date, we still return it as we can't show values in past data
    // Otherwise this will create confusion and data reliability issues
    return reversedData.find((item) => dayjs.utc(item.Date).isSame(TODAY.subtract(1, "day"), "day"));
};

const getLastOfPreviousMonth = (data: BacklinksTimeseriesListData = [], todayItem?: BacklinksTimeSeriesSummary) => {
    const compareItemDate = todayItem?.Date ? dayjs.utc(todayItem.Date) : TODAY;
    const lastDayOfPreviousMonth = compareItemDate.subtract(1, "month").endOf("month");
    return data.find((item) => dayjs.utc(item.Date).isSame(lastDayOfPreviousMonth, "day"));
};

export const BacklinksOverviewSummaryMetrics: FC = () => {
    const params = useExpectedBacklinksParams();

    const translate = useTranslation();
    const { data, isLoading } = useBacklinksTimeseriesApi({
        key: params.key,
        from: START_DATE_13_M.format(API_DATE_REQUEST_FORMAT),
        to: TODAY.format(API_DATE_REQUEST_FORMAT),
        isDaily: true,
    });

    const todayItem = getTodayItemOrClosestWithData(data);
    const lastOfPreviousMonth = getLastOfPreviousMonth(data, todayItem);

    const refDomainsSummaryValues = {
        current: todayItem?.ReferringDomains ?? 0,
        previous: lastOfPreviousMonth?.ReferringDomains ?? 0,
    };

    const backlinksSummaryValues = {
        current: todayItem?.Backlinks ?? 0,
        previous: lastOfPreviousMonth?.Backlinks ?? 0,
    };

    const DTSSummaryValues = {
        current: todayItem?.Rank ?? 0,
        previous: lastOfPreviousMonth?.Rank ?? 0,
    };

    return (
        <BacklinksOverviewTopWidgetsContainer>
            {isLoading ? (
                <BacklinksOverviewTopWidgetsEmptyContainer>
                    <PixelPlaceholderLoader
                        width="90%"
                        height="20px"
                    />
                </BacklinksOverviewTopWidgetsEmptyContainer>
            ) : (
                <Fragment>
                    <BacklinksSingleMetricWidget
                        title={translate("digitalsuite.backlinks.overview.refdomains_widget.title")}
                        tooltip={translate("digitalsuite.backlinks.overview.refdomains_widget.tooltip")}
                        currentValue={refDomainsSummaryValues.current}
                        previousValue={refDomainsSummaryValues.previous}
                        dataAutomationMap={{
                            container: "referring-domain-card",
                            title: "referring-domain-title",
                            value: "referring-domain-value",
                            change: "referring-domain-change",
                        }}
                    />
                    <BacklinksSingleMetricWidget
                        title={translate("digitalsuite.backlinks.overview.backlinks_widget.title")}
                        tooltip={translate("digitalsuite.backlinks.overview.backlinks_widget.tooltip")}
                        currentValue={backlinksSummaryValues.current}
                        previousValue={backlinksSummaryValues.previous}
                        dataAutomationMap={{
                            container: "backlinks-card",
                            title: "backlinks-title",
                            value: "backlinks-value",
                            change: "backlinks-change",
                        }}
                    />
                    <BacklinksSingleMetricWidget
                        title={translate("digitalsuite.backlinks.overview.dts_widget.title")}
                        tooltip={translate("digitalsuite.backlinks.overview.dts_widget.tooltip")}
                        currentValue={DTSSummaryValues.current}
                        previousValue={DTSSummaryValues.previous}
                        dataAutomationMap={{
                            container: "domain-authority-card",
                            title: "domain-authority-title",
                            value: "domain-authority-value",
                            change: "domain-authority-change",
                        }}
                    />
                </Fragment>
            )}
        </BacklinksOverviewTopWidgetsContainer>
    );
};
